// More than four skin influences per vertex — what an Unreal export carries and
// three.js drops.
//
// GLTFLoader binds JOINTS_0 / WEIGHTS_0 as `skinIndex` / `skinWeight` and hands
// every further set back as a plain attribute it does not understand
// (`joints_1`, `weights_1`, ...; it lowercases unknown names). three's built-in
// skinning reads four influences and nothing else. A MetaHuman face is skinned
// to up to twelve: measured on Aoi's export, three sets on every face primitive,
// 6.8 influences per vertex on average, and the FIRST FOUR CARRY 89% of a
// vertex's weight on average and as little as 39% — Unreal writes the sets in
// descending weight order, so a top-four reduction gains nothing over what the
// loader already keeps. `repairSkinWeights` then scales those four up to 1.0,
// which is exact at rest and wrong the moment a bone moves: a vertex that owed
// 60% of itself to eight other joints follows four with full weight, and the
// posed cheek breaks into shards where the face joints overlap. The body skin
// has the same three sets, so a walk cycle is affected in the same way, less
// visibly.
//
// The fix is to use the influences the file carries: adopt the extra sets as
// `skinIndex1/skinWeight1/skinIndex2/skinWeight2`, renormalise the weights across
// ALL sets (not set 0 alone), and extend three's four skinning chunks to sum the
// extra sets behind per-material defines (`AOS_SKIN_SETS_2`, `AOS_SKIN_SETS_3`).
// The defines are per MATERIAL and the attributes per GEOMETRY, and they must
// agree: a shader that declares `skinIndex1` on a geometry that lacks it reads
// WebGL's constant default (0,0,0,1) — bone 0 at full weight — so every skinned
// mesh of a rig is padded to the rig's maximum set count before any material on
// it gets the define (`padSkinSets`), and the define is set only on the
// per-character materials the mapping binds, never on a shared placeholder.
import * as THREE from 'three'
import { skinWeightsNeedRepair } from './skinWeights.js'

/** glTF allows any number of sets; three sets (12 influences) is what Unreal writes. */
export const MAX_SKIN_SETS = 3
/** Attribute names GLTFLoader gives the extra glTF sets (lowercased unknowns). */
const LOADER_JOINTS = (n) => `joints_${n}`
const LOADER_WEIGHTS = (n) => `weights_${n}`
/** Attribute names the extended chunks read for set n >= 1. */
export const skinIndexAttr = (n) => (n === 0 ? 'skinIndex' : `skinIndex${n}`)
export const skinWeightAttr = (n) => (n === 0 ? 'skinWeight' : `skinWeight${n}`)
/** Define enabling set n (2 or 3 sets) on a material. */
export const skinSetsDefine = (n) => `AOS_SKIN_SETS_${n}`

/**
 * Adopts every extra glTF joint set the loader left on the geometry and
 * renormalises the weights across all sets so they sum to 1 per vertex.
 * Returns the number of sets now present (1 when the geometry has only the
 * standard pair). Idempotent.
 */
export function adoptExtraSkinSets(geometry) {
  const attrs = geometry?.attributes
  if (!attrs?.skinIndex || !attrs?.skinWeight) return 0
  for (let n = 1; n < MAX_SKIN_SETS; n++) {
    const ji = attrs[LOADER_JOINTS(n)], wi = attrs[LOADER_WEIGHTS(n)]
    if (ji && wi) {
      geometry.setAttribute(skinIndexAttr(n), ji)
      geometry.setAttribute(skinWeightAttr(n), wi)
      geometry.deleteAttribute(LOADER_JOINTS(n))
      geometry.deleteAttribute(LOADER_WEIGHTS(n))
    }
  }
  let sets = 1
  while (sets < MAX_SKIN_SETS && attrs[skinIndexAttr(sets)] && attrs[skinWeightAttr(sets)]) sets++
  // Across sets when there are several; a lone quantized set gets the same
  // renormalisation repairSkinWeights would have given it (skinWeights.js).
  if (sets > 1 || skinWeightsNeedRepair(attrs.skinWeight)) renormalizeAcrossSets(geometry, sets)
  geometry.userData.skinSets = sets
  return sets
}

/**
 * Rewrites every set's weights as float32 so that a vertex's weights sum to 1
 * across ALL sets. Normalised-integer sources (the quantized weights an Unreal
 * export carries) are read through the attribute's own normalisation.
 */
export function renormalizeAcrossSets(geometry, sets) {
  const attrs = geometry.attributes
  const count = attrs.skinWeight.count
  const src = [], out = []
  for (let n = 0; n < sets; n++) {
    const a = attrs[skinWeightAttr(n)]
    src.push(a)
    out.push(new Float32Array(count * 4))
  }
  const read = (a, v, k) => a.normalized ? THREE.MathUtils.denormalize(a.array[v * a.itemSize + k], a.array) : a.array[v * a.itemSize + k]
  for (let v = 0; v < count; v++) {
    let sum = 0
    for (let n = 0; n < sets; n++) for (let k = 0; k < 4; k++) sum += read(src[n], v, k)
    const inv = sum > 0 ? 1 / sum : 0
    for (let n = 0; n < sets; n++) for (let k = 0; k < 4; k++) out[n][v * 4 + k] = read(src[n], v, k) * inv
  }
  for (let n = 0; n < sets; n++) geometry.setAttribute(skinWeightAttr(n), new THREE.BufferAttribute(out[n], 4, false))
}

/**
 * Gives a geometry zero-weight attributes for every set up to `sets`, so a
 * material carrying the set defines reads real zeros rather than WebGL's
 * (0,0,0,1) default for a missing attribute. Returns how many sets were added.
 */
export function padSkinSets(geometry, sets) {
  const attrs = geometry?.attributes
  if (!attrs?.skinIndex) return 0
  let added = 0
  const count = attrs.skinIndex.count
  for (let n = 1; n < sets; n++) {
    if (attrs[skinIndexAttr(n)] && attrs[skinWeightAttr(n)]) continue
    geometry.setAttribute(skinIndexAttr(n), new THREE.BufferAttribute(new Uint16Array(count * 4), 4, false))
    geometry.setAttribute(skinWeightAttr(n), new THREE.BufferAttribute(new Float32Array(count * 4), 4, false))
    added++
  }
  geometry.userData.skinSets = Math.max(geometry.userData.skinSets ?? 1, sets)
  return added
}

const MARK = '// AOS_SKIN_SETS'

/**
 * Extends three's skinning chunks to read up to three sets behind the defines.
 * Patches the shared ShaderChunk table once (idempotent), so every material that
 * includes the standard chunks — the compiled graph materials do — picks it up
 * when its defines say so, and is byte-identical to stock three when they don't.
 */
export function installExtendedSkinning(chunks = THREE.ShaderChunk) {
  if (chunks.skinning_pars_vertex.includes(MARK)) return false
  const pars = `
#ifdef USE_SKINNING
	${MARK}
	#ifdef AOS_SKIN_SETS_2
	attribute vec4 skinIndex1;
	attribute vec4 skinWeight1;
	#endif
	#ifdef AOS_SKIN_SETS_3
	attribute vec4 skinIndex2;
	attribute vec4 skinWeight2;
	#endif
#endif
`
  const base = `
#ifdef USE_SKINNING
	#ifdef AOS_SKIN_SETS_2
	mat4 boneMat1X = getBoneMatrix( skinIndex1.x );
	mat4 boneMat1Y = getBoneMatrix( skinIndex1.y );
	mat4 boneMat1Z = getBoneMatrix( skinIndex1.z );
	mat4 boneMat1W = getBoneMatrix( skinIndex1.w );
	#endif
	#ifdef AOS_SKIN_SETS_3
	mat4 boneMat2X = getBoneMatrix( skinIndex2.x );
	mat4 boneMat2Y = getBoneMatrix( skinIndex2.y );
	mat4 boneMat2Z = getBoneMatrix( skinIndex2.z );
	mat4 boneMat2W = getBoneMatrix( skinIndex2.w );
	#endif
#endif
`
  // skinning_vertex: the extra terms go in before `transformed = ( bindMatrixInverse * skinned ).xyz;`
  const skinTerms = `
	#ifdef AOS_SKIN_SETS_2
	skinned += boneMat1X * skinVertex * skinWeight1.x;
	skinned += boneMat1Y * skinVertex * skinWeight1.y;
	skinned += boneMat1Z * skinVertex * skinWeight1.z;
	skinned += boneMat1W * skinVertex * skinWeight1.w;
	#endif
	#ifdef AOS_SKIN_SETS_3
	skinned += boneMat2X * skinVertex * skinWeight2.x;
	skinned += boneMat2Y * skinVertex * skinWeight2.y;
	skinned += boneMat2Z * skinVertex * skinWeight2.z;
	skinned += boneMat2W * skinVertex * skinWeight2.w;
	#endif
	transformed = ( bindMatrixInverse * skinned ).xyz;`
  // skinnormal_vertex: likewise before `skinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;`
  const normalTerms = `
	#ifdef AOS_SKIN_SETS_2
	skinMatrix += skinWeight1.x * boneMat1X;
	skinMatrix += skinWeight1.y * boneMat1Y;
	skinMatrix += skinWeight1.z * boneMat1Z;
	skinMatrix += skinWeight1.w * boneMat1W;
	#endif
	#ifdef AOS_SKIN_SETS_3
	skinMatrix += skinWeight2.x * boneMat2X;
	skinMatrix += skinWeight2.y * boneMat2Y;
	skinMatrix += skinWeight2.z * boneMat2Z;
	skinMatrix += skinWeight2.w * boneMat2W;
	#endif
	skinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;`
  const v = chunks.skinning_vertex, nrm = chunks.skinnormal_vertex
  const vAnchor = 'transformed = ( bindMatrixInverse * skinned ).xyz;'
  const nAnchor = 'skinMatrix = bindMatrixInverse * skinMatrix * bindMatrix;'
  if (!v.includes(vAnchor) || !nrm.includes(nAnchor)) {
    throw new Error('[skinInfluences] three changed its skinning chunks; the extended skinning cannot be installed')
  }
  chunks.skinning_pars_vertex = chunks.skinning_pars_vertex + pars
  chunks.skinbase_vertex = chunks.skinbase_vertex + base
  chunks.skinning_vertex = v.replace(vAnchor, skinTerms.trimStart())
  chunks.skinnormal_vertex = nrm.replace(nAnchor, normalTerms.trimStart())
  return true
}

/**
 * Turns the extra sets on for one material. `sets` is the geometry's count
 * (1..3); 1 removes the defines. Returns true when the defines changed, which
 * the caller must follow with `material.needsUpdate = true` (done here).
 */
export function enableExtendedSkinning(material, sets) {
  if (!material) return false
  const d = (material.defines = material.defines || {})
  let changed = false
  for (let n = 2; n <= MAX_SKIN_SETS; n++) {
    const key = skinSetsDefine(n), want = sets >= n
    if (want && !(key in d)) { d[key] = ''; changed = true }
    if (!want && key in d) { delete d[key]; changed = true }
  }
  if (changed) material.needsUpdate = true
  return changed
}

/**
 * The rig-level pass: adopt every skinned mesh's extra sets, pad all of them to
 * the rig's maximum so one material may serve any of them, and report.
 * @returns {{ sets:number, meshes:number, extended:number }}
 */
export function prepareRigSkinSets(root) {
  const skinned = []
  root.traverse((o) => { if (o.isSkinnedMesh && o.geometry) skinned.push(o) })
  let sets = 1
  for (const m of skinned) sets = Math.max(sets, adoptExtraSkinSets(m.geometry))
  if (sets > 1) {
    installExtendedSkinning()
    for (const m of skinned) padSkinSets(m.geometry, sets)
  }
  return { sets, meshes: skinned.length, extended: skinned.filter((m) => (m.geometry.userData.skinSets ?? 1) > 1).length }
}
